// QueuePromptsAsCustomer.tsx - Queue Prompts component with login-as-customer support
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  Snackbar,
  Fade,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  VolumeUp as VolumeIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { useLocation } from 'react-router-dom';
import errorLogger from '../../services/errorLogger.service';

// Create logger for this component
const logger = errorLogger.createComponentLogger('QueuePromptsAsCustomer');

interface Voice {
  voice_id: string;
  name: string;
  category: string;
  preview_url?: string;
}

interface QueueRecording {
  RecordID: number;
  Name: string;
  Notes?: string;
  usage_type: 'moh' | 'announcement' | 'both';
  Created: string;
  deleted: boolean;
  has_blob: boolean;
  audio_size: number;
}

function QueuePromptsAsCustomer() {
  const { member, isLoginAsCustomer } = useAuthWithCustomerOverride();
  const customerFilter = useCustomerFilter();
  const location = useLocation();

  // Check for login token on initialization
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = !!sessionStorage.getItem('loginAsCustomer');
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      logger.debug('Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });

  // Track last fetched customer ID to prevent unnecessary refetches
  const [lastFetchedCustomerId, setLastFetchedCustomerId] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const blobUrlRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // TTS State
  const [text, setText] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('');
  const [voices, setVoices] = useState<Voice[]>([]);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const voiceSettings = {
    stability: 0.5,
    similarity_boost: 0.75,
    style: 0,
    use_speaker_boost: true,
  };
  const [fileName, setFileName] = useState('');
  const [notes, setNotes] = useState('');
  const [usageType, setUsageType] = useState<'moh' | 'announcement' | 'both'>('moh');
  const [savingAudio, setSavingAudio] = useState(false);
  const [cacheKey, setCacheKey] = useState<string | null>(null);
  const [hasGeneratedAudio, setHasGeneratedAudio] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tabValue, setTabValue] = useState(0);

  // Recordings List State
  const [recordings, setRecordings] = useState<QueueRecording[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecordings, setTotalRecordings] = useState(0);
  const [loadingRecordings, setLoadingRecordings] = useState(false);

  // Upload State
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadUsageType, setUploadUsageType] = useState<'moh' | 'announcement' | 'both'>('moh');
  const [uploading, setUploading] = useState(false);

  // Edit State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecording, setEditingRecording] = useState<QueueRecording | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editUsageType, setEditUsageType] = useState<'moh' | 'announcement' | 'both'>('moh');

  // Playback State
  const [playingRecordingId, setPlayingRecordingId] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Helper to get customer filter params
  const getCustomerParams = () => {
    const params: any = {};
    // SECURITY: ALWAYS pass customerId to prevent data leakage
    if (isLoginAsCustomer && member?.customerID) {
      params.customerId = member.customerID;
      logger.debug(`LOGIN AS MODE - Using customerID: ${member.customerID}`);
    } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
      params.customerId = customerFilter.customerId;
      logger.debug(`FILTER MODE - Using customerID: ${customerFilter.customerId}`);
    } else if (member?.customerID) {
      // Regular customer login - MUST pass their customer ID
      params.customerId = member.customerID;
      logger.debug(`CUSTOMER LOGIN - Using customerID: ${member.customerID}`);
    } else {
      logger.error('SECURITY WARNING: No customer ID available!');
    }
    return params;
  };

  // Helper function to show snackbar
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const fetchRecordings = async () => {
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Still waiting for login-as data, aborting fetch');
      return;
    }

    // Verify session data matches member if in login-as mode
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member?.customerID) {
          logger.error('CRITICAL: Customer ID mismatch!', {
            sessionCustomerId: parsed.customerId,
            memberCustomerId: member?.customerID
          });
          setError('Customer context mismatch - please refresh the page');
          setLoadingRecordings(false);
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }

    setLoadingRecordings(true);
    try {
      const params: any = {
        page: String(page + 1),
        limit: String(rowsPerPage),
      };

      if (isLoginAsCustomer && member?.customerID) {
        logger.debug(`LOGIN AS MODE - Using customerID: ${member.customerID}`);
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        logger.debug(`FILTER MODE - Using customerFilter: ${customerFilter.customerId}`);
        params.customerId = customerFilter.customerId;
      } else {
        logger.debug('NO FILTER - Will use JWT customer');
      }

      logger.debug('Fetching recordings with params:', params);
      const response = await api.get('/endpoints/queue-prompts/recordings', { params });

      if (response.data.success) {
        setRecordings(response.data.data.data || []);
        setTotalRecordings(response.data.data.total || 0);
        setLastFetchedCustomerId(params.customerId || null);
      }
    } catch (err: any) {
      logger.error('Failed to fetch recordings:', err);
      setError(err.response?.data?.message || 'Failed to load recordings');
    } finally {
      setLoadingRecordings(false);
    }
  };

  const fetchVoices = async () => {
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Still waiting for login-as data, aborting voices fetch');
      return;
    }

    // Verify session data matches member if in login-as mode
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member?.customerID) {
          logger.error('CRITICAL: Customer ID mismatch during voices fetch!', {
            sessionCustomerId: parsed.customerId,
            memberCustomerId: member?.customerID
          });
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }

    try {
      const params = getCustomerParams();
      logger.debug('Fetching voices with params:', params);
      const response = await api.get('/endpoints/prompts/voices', { params });
      if (response.data.success) {
        setVoices(response.data.data);
        if (response.data.data.length > 0 && !selectedVoice) {
          setSelectedVoice(response.data.data[0].voice_id);
        }
      }
    } catch (err: any) {
      logger.error('Failed to fetch voices:', err);
      setError('Failed to load available voices');
    }
  };

  const generateAudio = async () => {
    if (!text.trim()) {
      setError('Please enter some text');
      return;
    }

    if (!selectedVoice) {
      setError('Please select a voice');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const params = getCustomerParams();

      const response = await api.post('/endpoints/prompts/generate', {
        text: text.trim(),
        voice_id: selectedVoice,
        voice_settings: voiceSettings,
      }, { params });

      logger.debug('TTS Response:', response.data);

      if (response.data.success) {
        const responseData = response.data.data;

        if (responseData && responseData.audio_url) {
          setAudioUrl(responseData.audio_url);
          setCacheKey(responseData.cache_key);
          setHasGeneratedAudio(true);
          showSnackbar(responseData.cached ? 'Audio retrieved from cache!' : 'Audio generated successfully!', 'success');
        } else {
          showSnackbar('Audio generated but URL not received', 'error');
          logger.error('Invalid response structure:', response.data);
        }
      }
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to generate audio', 'error');
    } finally {
      setLoading(false);
    }
  };

  const playAudio = async () => {
    logger.debug(`playAudio called - audioUrl: ${audioUrl}, cacheKey: ${cacheKey}`);
    if (!audioUrl || !cacheKey) {
      logger.error('Missing audioUrl or cacheKey');
      return;
    }

    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    try {
      const params = getCustomerParams();

      logger.debug(`Fetching audio from: /endpoints/prompts/preview/${cacheKey}.mp3`);
      const response = await api.get(`/endpoints/prompts/preview/${cacheKey}.mp3`, {
        responseType: 'blob',
        params
      });

      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }

      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const blobUrl = URL.createObjectURL(audioBlob);
      blobUrlRef.current = blobUrl;

      if (audioRef.current) {
        audioRef.current.src = blobUrl;
        audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      logger.error('Failed to load audio:', error);
      showSnackbar('Failed to load audio preview', 'error');
    }
  };

  const saveToAsterisk = async () => {
    if (!audioUrl || !cacheKey || !fileName.trim()) {
      setError('Please generate audio and enter a filename first');
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(fileName)) {
      setError('Filename can only contain letters, numbers, and underscores');
      return;
    }

    setSavingAudio(true);
    setError(null);
    setSuccess(null);

    try {
      logger.debug('Saving audio with:', { cache_key: cacheKey, filename: fileName, notes: notes, usage_type: usageType });
      const params = getCustomerParams();

      const response = await api.post('/endpoints/queue-prompts/save', {
        cache_key: cacheKey,
        filename: fileName,
        notes: notes.trim() || undefined,
        usage_type: usageType,
      }, { params });

      logger.debug('Save response:', response.data);

      if (response.data.success) {
        showSnackbar(`Audio saved as ${response.data.filename} and added to Queue Recordings`, 'success');
        setText('');
        setFileName('');
        setNotes('');
        setUsageType('moh');
        setAudioUrl(null);
        setCacheKey(null);
        setHasGeneratedAudio(false);
        setCurrentStep(0);
        fetchRecordings();
        setTabValue(0);
      }
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to save audio', 'error');
    } finally {
      setSavingAudio(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadFileName.trim()) {
      setError('Please select a file and enter a filename');
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(uploadFileName)) {
      setError('Filename can only contain letters, numbers, and underscores');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('filename', uploadFileName);
    formData.append('notes', uploadNotes);
    formData.append('usage_type', uploadUsageType);

    try {
      const params = getCustomerParams();

      const response = await api.post('/endpoints/queue-prompts/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params
      });

      if (response.data.success) {
        setSuccess('Recording uploaded successfully');
        setUploadDialogOpen(false);
        setUploadFile(null);
        setUploadFileName('');
        setUploadNotes('');
        setUploadUsageType('moh');
        fetchRecordings();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload recording');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (recording: QueueRecording) => {
    setEditingRecording(recording);
    setEditNotes(recording.Notes || '');
    setEditUsageType(recording.usage_type);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingRecording) return;

    try {
      const params = getCustomerParams();

      const response = await api.put(`/endpoints/queue-prompts/recordings/${editingRecording.RecordID}`, {
        notes: editNotes,
        usage_type: editUsageType,
      }, { params });

      if (response.data.success) {
        setSuccess('Recording updated successfully');
        setEditDialogOpen(false);
        setEditingRecording(null);
        fetchRecordings();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update recording');
    }
  };

  const handleDelete = async (recording: QueueRecording) => {
    if (!window.confirm(`Are you sure you want to delete "${recording.Name}"?`)) {
      return;
    }

    try {
      const params = getCustomerParams();

      const response = await api.delete(`/endpoints/queue-prompts/recordings/${recording.RecordID}`, { params });

      if (response.data.success) {
        setSuccess('Recording deleted successfully');
        fetchRecordings();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete recording');
    }
  };

  const handlePlayRecording = async (recording: QueueRecording) => {
    // If this recording is already playing, stop it
    if (playingRecordingId === recording.RecordID) {
      audioRef.current?.pause();
      setIsPlaying(false);
      setPlayingRecordingId(null);
      return;
    }

    try {
      // Stop any currently playing audio
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }

      logger.debug(`Fetching audio stream for recording: ${recording.RecordID}`);

      const params = getCustomerParams();

      const response = await api.get(`/endpoints/queue-prompts/stream/${recording.RecordID}`, {
        responseType: 'blob',
        params
      });

      logger.debug(`Audio response received, blob size: ${response.data.size}, type: ${response.data.type}`);

      if (response.data.size === 0) {
        throw new Error('Received empty audio file');
      }

      // Clean up previous blob URL if exists
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }

      // Create blob URL
      const blobUrl = URL.createObjectURL(response.data);
      blobUrlRef.current = blobUrl;
      logger.debug(`Created blob URL: ${blobUrl}`);

      if (audioRef.current) {
        audioRef.current.src = blobUrl;
        audioRef.current.load();

        setTimeout(async () => {
          try {
            if (audioRef.current) {
              const playPromise = audioRef.current.play();

              if (playPromise !== undefined) {
                playPromise
                  .then(() => {
                    logger.debug('Play promise resolved - audio should be playing');
                    setIsPlaying(true);
                    setPlayingRecordingId(recording.RecordID);
                  })
                  .catch((error) => {
                    logger.error('Play promise rejected:', error);
                    setError(`Failed to play audio: ${error.message}`);
                    setPlayingRecordingId(null);
                  });
              }
            }
          } catch (playError) {
            logger.error('Error in play attempt:', playError);
            setError('Failed to play audio');
            setPlayingRecordingId(null);
          }
        }, 100);
      }
    } catch (err: any) {
      logger.error('Error playing recording:', err);
      setError(err.response?.data?.message || err.message || 'Failed to play recording');
      setPlayingRecordingId(null);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setPlayingRecordingId(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getUsageTypeLabel = (usageType: string) => {
    switch (usageType) {
      case 'moh':
        return 'Music on Hold';
      case 'announcement':
        return 'Announcement';
      case 'both':
        return 'Both';
      default:
        return usageType;
    }
  };

  const getUsageTypeColor = (usageType: string): 'primary' | 'secondary' | 'success' => {
    switch (usageType) {
      case 'moh':
        return 'primary';
      case 'announcement':
        return 'secondary';
      case 'both':
        return 'success';
      default:
        return 'primary';
    }
  };

  // Fetch recordings when component mounts or dependencies change
  useEffect(() => {
    if (!member) return;

    // Check for login token
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoginToken = urlParams.has('lat');
    const sessionData = sessionStorage.getItem('loginAsCustomer');

    // If we have a login token but no session data, wait
    if (hasLoginToken && !sessionData) {
      logger.debug('Have login token but no session data - waiting...');
      return;
    }

    // If session data exists, verify it matches member
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member.customerID) {
          logger.debug('Session/member mismatch, waiting for sync...');
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }

    // Add small delay to let state settle
    const timer = setTimeout(() => {
      fetchRecordings();
    }, 200);

    return () => clearTimeout(timer);
  }, [member?.customerID, isLoginAsCustomer, page, rowsPerPage]); // Re-fetch when customer ID, login-as state, or pagination changes

  // Fetch voices on component mount
  useEffect(() => {
    if (!member) return;

    // Check for login token
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoginToken = urlParams.has('lat');
    const sessionData = sessionStorage.getItem('loginAsCustomer');

    // If we have a login token but no session data, wait
    if (hasLoginToken && !sessionData) {
      logger.debug('Have login token but no session data - waiting for voices...');
      return;
    }

    // If session data exists, verify it matches member
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member.customerID) {
          logger.debug('Session/member mismatch, waiting for sync (voices)...');
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }

    // Add small delay to let state settle
    const timer = setTimeout(() => {
      fetchVoices();
    }, 200);

    return () => clearTimeout(timer);
  }, [member?.customerID, isLoginAsCustomer]); // Re-fetch when customer ID or login-as state changes

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Queue Music & Announcements
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setTabValue(1);
              setCurrentStep(0);
              setText('');
              setFileName('');
              setNotes('');
              setUsageType('moh');
              if (!selectedVoice && voices.length > 0) {
                setSelectedVoice(voices[0].voice_id);
              }
              setAudioUrl(null);
              setHasGeneratedAudio(false);
            }}
          >
            Create Recording
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Audio
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchRecordings}
            disabled={loadingRecordings}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>Usage Type</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="center">Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingRecordings ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : recordings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No recordings found
                </TableCell>
              </TableRow>
            ) : (
              recordings.map((recording) => (
                <TableRow key={recording.RecordID}>
                  <TableCell>{recording.Name}</TableCell>
                  <TableCell>
                    <Chip
                      label={getUsageTypeLabel(recording.usage_type)}
                      color={getUsageTypeColor(recording.usage_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{recording.Notes || '-'}</TableCell>
                  <TableCell align="center">
                    {new Date(recording.Created).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handlePlayRecording(recording)}
                      title={playingRecordingId === recording.RecordID ? "Stop" : "Play"}
                      color={playingRecordingId === recording.RecordID ? "primary" : "default"}
                    >
                      {playingRecordingId === recording.RecordID ? <StopIcon /> : <PlayIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(recording)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(recording)}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalRecordings}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Audio element for playback */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        style={{ display: 'none' }}
      />

      {/* Create Recording Dialog with TTS */}
      <Dialog
        open={tabValue === 1}
        onClose={() => setTabValue(0)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5">
            Create Queue Audio
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Step 1: Filename and Usage Type */}
          {currentStep === 0 && (
            <Box>
              <Typography variant="h5" gutterBottom>
                Step 1: Configure Audio File
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Set the filename and select how this audio will be used in your queue.
              </Typography>
              <TextField
                fullWidth
                label="Filename (no extension)"
                value={fileName}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[a-zA-Z0-9_]*$/.test(value)) {
                    setFileName(value);
                  }
                }}
                placeholder="e.g., queue_music, hold_announcement"
                helperText={
                  fileName && !/^[a-zA-Z0-9_]+$/.test(fileName)
                    ? "Filename can only contain letters, numbers, and underscores"
                    : "Use letters, numbers, and underscores only"
                }
                error={fileName.length > 0 && !/^[a-zA-Z0-9_]+$/.test(fileName)}
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Usage Type</InputLabel>
                <Select
                  value={usageType}
                  onChange={(e) => setUsageType(e.target.value as 'moh' | 'announcement' | 'both')}
                  label="Usage Type"
                >
                  <MenuItem value="moh">Music on Hold</MenuItem>
                  <MenuItem value="announcement">Periodic Announcement</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Brief description of this audio file"
                multiline
                rows={2}
                sx={{ mb: 3 }}
              />
              <Button
                variant="contained"
                onClick={() => setCurrentStep(1)}
                disabled={!fileName.trim() || !/^[a-zA-Z0-9_]+$/.test(fileName)}
                fullWidth
                size="large"
              >
                Next: Choose Voice
              </Button>
            </Box>
          )}

          {/* Step 2: Voice Selection */}
          {currentStep === 1 && (
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Creating: <strong>{fileName}</strong> ({getUsageTypeLabel(usageType)})
              </Typography>
              <Typography variant="h5" gutterBottom>
                Step 2: Choose a Voice
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Select a voice for your recording. Click on any voice to hear a sample.
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {voices.map((voice) => (
                  <Grid item xs={12} sm={6} md={4} key={voice.voice_id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: selectedVoice === voice.voice_id ? 2 : 0,
                        borderColor: 'primary.main',
                        transition: 'all 0.2s',
                        '&:hover': { boxShadow: 3 }
                      }}
                      onClick={() => {
                        setSelectedVoice(voice.voice_id);
                        if (voice.preview_url && audioRef.current) {
                          audioRef.current.src = voice.preview_url;
                          audioRef.current.play();
                        }
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6">{voice.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {voice.category}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setCurrentStep(0)}
                  sx={{ flex: 1 }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setCurrentStep(2)}
                  disabled={!selectedVoice}
                  sx={{ flex: 1 }}
                >
                  Next: Enter Text
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 3: Text Entry and Generation */}
          {currentStep === 2 && (
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                File: <strong>{fileName}</strong> | Voice: <strong>{voices.find(v => v.voice_id === selectedVoice)?.name}</strong> | Type: <strong>{getUsageTypeLabel(usageType)}</strong>
              </Typography>
              <Typography variant="h5" gutterBottom>
                Step 3: Enter Your Message
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Type the message you want callers to hear while in the queue.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={8}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Thank you for holding. Your call is important to us..."
                variant="outlined"
                inputProps={{ maxLength: 400 }}
                helperText={`${text.length} / 400 characters`}
                error={text.length > 400}
                sx={{ mb: 3 }}
              />

              {!hasGeneratedAudio ? (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setCurrentStep(1)}
                    sx={{ flex: 1 }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={generateAudio}
                    disabled={loading || !text.trim()}
                    startIcon={loading ? <CircularProgress size={20} /> : <VolumeIcon />}
                    sx={{ flex: 2 }}
                  >
                    {loading ? 'Generating...' : 'Generate Audio'}
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Audio generated successfully! Preview your recording below.
                  </Alert>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setHasGeneratedAudio(false);
                        setAudioUrl(null);
                      }}
                    >
                      Regenerate
                    </Button>
                    <Button
                      variant="contained"
                      onClick={playAudio}
                      disabled={loading}
                      startIcon={isPlaying ? <StopIcon /> : <PlayIcon />}
                      sx={{ flex: 1 }}
                    >
                      {isPlaying ? 'Stop' : 'Play Audio'}
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={saveToAsterisk}
                      disabled={savingAudio}
                      startIcon={savingAudio ? <CircularProgress size={20} /> : <SaveIcon />}
                      sx={{ flex: 1 }}
                    >
                      {savingAudio ? 'Saving...' : 'Save to System'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTabValue(0)} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Queue Audio</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/mp3,audio/mpeg,audio/wav,audio/x-wav"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setUploadFile(file);
                  // Auto-fill filename from file name
                  const name = file.name.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9_]/g, '_');
                  setUploadFileName(name);
                }
              }}
            />
            <Button
              fullWidth
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              sx={{ mb: 2 }}
            >
              {uploadFile ? uploadFile.name : 'Select MP3 or WAV File'}
            </Button>

            <TextField
              fullWidth
              label="Filename (no extension)"
              value={uploadFileName}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow letters, numbers, and underscores
                if (/^[a-zA-Z0-9_]*$/.test(value)) {
                  setUploadFileName(value);
                }
              }}
              placeholder="e.g., queue_music, position_announcement"
              helperText={
                uploadFileName && !/^[a-zA-Z0-9_]+$/.test(uploadFileName)
                  ? "Filename can only contain letters, numbers, and underscores"
                  : "Use letters, numbers, and underscores only"
              }
              error={uploadFileName.length > 0 && !/^[a-zA-Z0-9_]+$/.test(uploadFileName)}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Usage Type</InputLabel>
              <Select
                value={uploadUsageType}
                onChange={(e) => setUploadUsageType(e.target.value as 'moh' | 'announcement' | 'both')}
                label="Usage Type"
              >
                <MenuItem value="moh">Music on Hold</MenuItem>
                <MenuItem value="announcement">Periodic Announcement</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Notes (optional)"
              value={uploadNotes}
              onChange={(e) => setUploadNotes(e.target.value)}
              placeholder="Description of this recording"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || !uploadFile || !uploadFileName.trim() || !/^[a-zA-Z0-9_]+$/.test(uploadFileName)}
            startIcon={uploading && <CircularProgress size={20} />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Queue Audio</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              File: {editingRecording?.Name}
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Usage Type</InputLabel>
              <Select
                value={editUsageType}
                onChange={(e) => setEditUsageType(e.target.value as 'moh' | 'announcement' | 'both')}
                label="Usage Type"
              >
                <MenuItem value="moh">Music on Hold</MenuItem>
                <MenuItem value="announcement">Periodic Announcement</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Notes"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Description of this recording"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default QueuePromptsAsCustomer;
